import { useState } from 'react'
import { KeyRound, LogIn, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button, EmptyState, Modal, Select, toast } from '@/components/ui'
import { useAuth } from '@/shell/module-system'
import { logout } from '@/shell/services/auth'
import {
  useAdminRoles,
  useAdminUsers,
  useConnectAs,
  useDeleteUser,
  useSetUserPassword,
  useUpdateUser,
  useUpsertUser,
} from '../api/queries'
import type { AdminUser, UserRole } from '../api/types'

export function UsersAndRolesSection() {
  const me = useAuth()
  const { data: usersData, isLoading } = useAdminUsers()
  const { data: rolesData } = useAdminRoles()
  const upsert = useUpsertUser()
  const remove = useDeleteUser()
  const connectAs = useConnectAs()
  const setPassword = useSetUserPassword()
  const update = useUpdateUser()

  const [pendingRemoval, setPendingRemoval] = useState<AdminUser | null>(null)
  const [pendingConnect, setPendingConnect] = useState<AdminUser | null>(null)
  const [pendingPassword, setPendingPassword] = useState<AdminUser | null>(null)
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null)
  const [addOpen, setAddOpen] = useState(false)

  const users = usersData?.users ?? []
  const roleIds = Object.keys(rolesData?.roles ?? {}) as UserRole[]

  function changeRole(user: AdminUser, role: UserRole) {
    upsert.mutate(
      { email: user.email, role, name: user.name ?? undefined },
      {
        onSuccess: () => toast.success(`${user.email} → ${role}`),
        onError: (err) =>
          toast.danger(
            `Role change failed${err instanceof Error ? `: ${err.message}` : ''}`,
          ),
      },
    )
  }

  function confirmRemove() {
    if (!pendingRemoval) return
    const u = pendingRemoval
    remove.mutate(u.email, {
      onSuccess: () => {
        toast.success(`Removed ${u.email}`)
        setPendingRemoval(null)
      },
      onError: (err) => {
        toast.danger(
          `Remove failed${err instanceof Error ? `: ${err.message}` : ''}`,
        )
      },
    })
  }

  function confirmConnect() {
    if (!pendingConnect) return
    const u = pendingConnect
    connectAs.mutate(u.email, {
      onError: (err) => {
        toast.danger(
          `Connect-as failed${err instanceof Error ? `: ${err.message}` : ''}`,
        )
        setPendingConnect(null)
      },
      // No onSuccess toast — useConnectAs hard-navigates to /dashboard.
    })
  }

  return (
    <section className="flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-primary">Users & Roles</h2>
          <p className="text-sm text-secondary">
            Assign one of {roleIds.length} roles per SPI user. Removing reverts
            the user to <code >viewer</code>.
          </p>
        </div>
        <Button variant="primary" onClick={() => setAddOpen(true)}>
          <Plus size={12} /> Add user
        </Button>
      </header>

      {isLoading ? (
        <div className="py-6 text-center text-sm text-tertiary">Loading…</div>
      ) : users.length === 0 ? (
        <EmptyState
          variant="no-data"
          title="No users yet"
          description="Add the first user with the button above."
        />
      ) : (
        <div className="overflow-hidden rounded-sm border border-border-subtle bg-surface">
          <table className="w-full border-collapse text-sm">
            <thead className="border-b border-border-subtle bg-surface-sunk">
              <tr className="text-left">
                <th className="px-3 py-2 text-role-section-label">
                  Email
                </th>
                <th className="px-3 py-2 text-role-section-label">
                  Name
                </th>
                <th className="px-3 py-2 text-role-section-label">
                  Role
                </th>
                <th className="px-3 py-2 text-role-section-label">
                  Added
                </th>
                <th className="w-24" />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.email}
                  className="border-b border-border-subtle last:border-b-0"
                >
                  <td className="px-3 py-2">
                    <div className="text-primary">{u.email}</div>
                    {u.odoo_login ? (
                      <div className="text-role-metadata">
                        Odoo: {u.odoo_login}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-secondary">{u.name ?? '—'}</td>
                  <td className="px-3 py-2">
                    <Select
                      aria-label={`Role for ${u.email}`}
                      value={u.role}
                      onChange={(e) => changeRole(u, e.target.value as UserRole)}
                    >
                      {roleIds.map((r) => (
                        <option key={r} value={r}>
                          {rolesData?.roles[r]?.label ?? r}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td className="px-3 py-2 text-role-metadata">
                    {u.created_at ? u.created_at.slice(0, 10) : '—'}
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 text-right">
                    {(() => {
                      const isSelf =
                        (me.email ?? '').toLowerCase() === u.email.toLowerCase()
                      const isAdminTarget = u.role === 'admin'
                      const disabled = isSelf || isAdminTarget
                      const reason = isSelf
                        ? 'Cannot impersonate yourself'
                        : isAdminTarget
                          ? 'Cannot impersonate another admin'
                          : `Connect as ${u.email}`
                      return (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconOnly
                          aria-label={reason}
                          title={reason}
                          disabled={disabled}
                          onClick={() => !disabled && setPendingConnect(u)}
                        >
                          <LogIn size={14} />
                        </Button>
                      )
                    })()}
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      aria-label={`Edit ${u.email}`}
                      title={`Edit ${u.email}`}
                      onClick={() => setEditingUser(u)}
                    >
                      <Pencil size={14} />
                    </Button>
                    {rolesData?.roles[u.role]?.vendor ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        aria-label={`Set password for ${u.email}`}
                        title={`Set / reset password for ${u.email}`}
                        onClick={() => setPendingPassword(u)}
                      >
                        <KeyRound size={14} />
                      </Button>
                    ) : null}
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      aria-label={`Remove ${u.email}`}
                      onClick={() => setPendingRemoval(u)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rolesData ? (
        <details className="rounded-sm border border-border-subtle bg-surface-sunk p-3">
          <summary className="cursor-pointer text-role-section-label">
            Role definitions
          </summary>
          <dl className="mt-3 grid grid-cols-[160px_1fr] gap-x-4 gap-y-2 text-sm">
            {Object.entries(rolesData.roles).map(([id, def]) => (
              <div key={id} className="contents">
                <dt className="text-primary">{def.label}</dt>
                <dd className="text-secondary">
                  <code className="text-2xs">{id}</code> · pages:{' '}
                  {def.pages.join(', ')} · chat:{' '}
                  {def.can_chat ? 'yes' : 'no'} · confirm:{' '}
                  {def.can_confirm_actions ? 'yes' : 'no'}
                </dd>
              </div>
            ))}
          </dl>
        </details>
      ) : null}

      {pendingConnect ? (
        <Modal
          open={pendingConnect !== null}
          onOpenChange={(open) => !open && setPendingConnect(null)}
          tone="info"
          title="Connect as user"
          description={
            <>
              You will see the SPA as{' '}
              <strong>{pendingConnect.name || pendingConnect.email}</strong>{' '}
              (<code>{pendingConnect.role}</code>). Their scoped data,
              page access, and permissions will apply for the next hour.
              All actions you take are logged with both your identity and
              theirs. Click "Exit impersonation" in the banner to return.
            </>
          }
          footer={
            <>
              <Button variant="ghost" onClick={() => setPendingConnect(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={confirmConnect}
                loading={connectAs.isPending}
              >
                Connect as {pendingConnect.name || pendingConnect.email}
              </Button>
            </>
          }
        />
      ) : null}

      {pendingRemoval ? (
        <Modal
          open={pendingRemoval !== null}
          onOpenChange={(open) => !open && setPendingRemoval(null)}
          tone="danger"
          title="Remove user"
          description={
            <>
              Remove <code >{pendingRemoval.email}</code>?
              Their role will revert to <code >viewer</code>.
              You can add them back at any time.
            </>
          }
          footer={
            <>
              <Button variant="ghost" onClick={() => setPendingRemoval(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={confirmRemove}
                loading={remove.isPending}
              >
                Remove
              </Button>
            </>
          }
        />
      ) : null}

      <AddUserModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        roleIds={roleIds}
        onSubmit={(email, role, name, odooLogin) => {
          upsert.mutate(
            { email, role, name, odoo_login: odooLogin },
            {
              onSuccess: () => {
                toast.success(`Added ${email}`)
                setAddOpen(false)
              },
              onError: (err) =>
                toast.danger(
                  `Add failed${err instanceof Error ? `: ${err.message}` : ''}`,
                ),
            },
          )
        }}
        submitting={upsert.isPending}
      />

      {editingUser ? (
        <EditUserModal
          user={editingUser}
          isSelf={(me.email ?? '').toLowerCase() === editingUser.email.toLowerCase()}
          submitting={update.isPending}
          onClose={() => setEditingUser(null)}
          onSubmit={(name, newEmail, odooLogin) => {
            const u = editingUser
            update.mutate(
              { email: u.email, name, new_email: newEmail, odoo_login: odooLogin },
              {
                onSuccess: (res) => {
                  if (res.self_rename) {
                    logout()
                    return
                  }
                  toast.success(
                    newEmail.toLowerCase() !== u.email.toLowerCase()
                      ? `Updated ${u.email} → ${newEmail}`
                      : `Updated ${u.email}`,
                  )
                  setEditingUser(null)
                },
                onError: (err) =>
                  toast.danger(
                    `Update failed${err instanceof Error ? `: ${err.message}` : ''}`,
                  ),
              },
            )
          }}
        />
      ) : null}

      {pendingPassword ? (
        <SetPasswordModal
          user={pendingPassword}
          onClose={() => setPendingPassword(null)}
          submitting={setPassword.isPending}
          onSubmit={(password) => {
            const u = pendingPassword
            setPassword.mutate(
              { email: u.email, new_password: password },
              {
                onSuccess: () => {
                  toast.success(`Password set for ${u.email}`)
                  setPendingPassword(null)
                },
                onError: (err) =>
                  toast.danger(
                    `Set password failed${err instanceof Error ? `: ${err.message}` : ''}`,
                  ),
              },
            )
          }}
        />
      ) : null}
    </section>
  )
}

const MIN_PASSWORD_LEN = 10

/** Crypto-strong password the admin can hand to the vendor. Avoids
 *  ambiguous characters (0/O, 1/l/I) so it's safe to read aloud. */
function generatePassword(len = 16): string {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*'
  const out = new Uint32Array(len)
  crypto.getRandomValues(out)
  return Array.from(out, (n) => chars[n % chars.length]).join('')
}

function SetPasswordModal({
  user,
  onClose,
  onSubmit,
  submitting,
}: {
  user: AdminUser
  onClose: () => void
  onSubmit: (password: string) => void
  submitting: boolean
}) {
  const [password, setPassword] = useState('')
  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LEN

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(password)
      toast.success('Password copied to clipboard')
    } catch {
      toast.danger('Could not copy — select and copy manually')
    }
  }

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      tone="info"
      title="Set / reset password"
      description={
        <>
          Set the login password for the vendor account{' '}
          <strong>{user.name || user.email}</strong> (<code>{user.role}</code>).
          No email is sent — copy the password and share it securely. The user
          can change it later via "Forgot password?" on the login screen.
        </>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={submitting}
            disabled={password.length < MIN_PASSWORD_LEN}
            onClick={() => onSubmit(password)}
          >
            Set password
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">New password</span>
          <input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={`At least ${MIN_PASSWORD_LEN} characters`}
            className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
            autoFocus
          />
          {tooShort ? (
            <span className="text-role-metadata text-danger">
              Must be at least {MIN_PASSWORD_LEN} characters.
            </span>
          ) : null}
        </label>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPassword(generatePassword())}
          >
            Generate
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={!password}
            onClick={copyToClipboard}
          >
            Copy
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function EditUserModal({
  user,
  isSelf,
  onClose,
  onSubmit,
  submitting,
}: {
  user: AdminUser
  isSelf: boolean
  onClose: () => void
  onSubmit: (name: string | null, newEmail: string, odooLogin: string | null) => void
  submitting: boolean
}) {
  const [email, setEmail] = useState(user.email)
  const [name, setName] = useState(user.name ?? '')
  const [odooLogin, setOdooLogin] = useState(user.odoo_login ?? '')

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      tone="info"
      title="Edit user"
      description={`Update ${user.email}'s name, email, or Odoo login. Role changes stay on the row's Role dropdown.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={submitting}
            disabled={!email.trim()}
            onClick={() =>
              onSubmit(name.trim() || null, email.trim().toLowerCase(), odooLogin.trim() || null)
            }
          >
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
            autoFocus
          />
          {isSelf ? (
            <span className="text-role-metadata">
              You're editing your own email — you'll be signed out immediately
              after saving so you can sign back in with the new address.
            </span>
          ) : null}
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Odoo login (optional)</span>
          <input
            type="email"
            value={odooLogin}
            onChange={(e) => setOdooLogin(e.target.value)}
            placeholder="leave blank if same as email"
            className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
          />
        </label>
      </div>
    </Modal>
  )
}

function AddUserModal({
  open,
  onClose,
  roleIds,
  onSubmit,
  submitting,
}: {
  open: boolean
  onClose: () => void
  roleIds: UserRole[]
  onSubmit: (
    email: string,
    role: UserRole,
    name: string | null,
    odooLogin: string | null,
  ) => void
  submitting: boolean
}) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<UserRole>('viewer')
  const [name, setName] = useState('')
  const [odooLogin, setOdooLogin] = useState('')

  function reset() {
    setEmail('')
    setRole('viewer')
    setName('')
    setOdooLogin('')
  }

  if (!open) return null
  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          reset()
          onClose()
        }
      }}
      tone="info"
      title="Add user"
      description="Assign a SPI role. The user will be able to log in with this email."
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              reset()
              onClose()
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={submitting}
            disabled={!email.trim()}
            onClick={() => {
              onSubmit(
                email.trim(),
                role,
                name.trim() || null,
                odooLogin.trim() || null,
              )
            }}
          >
            Add
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="person@siliconesplus.com"
            className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
            autoFocus
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Name (optional)</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Role</span>
          <Select value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
            {roleIds.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-secondary">Odoo login (optional)</span>
          <input
            type="email"
            value={odooLogin}
            onChange={(e) => setOdooLogin(e.target.value)}
            placeholder="leave blank if same as email"
            className="rounded-xs border border-border-default bg-surface px-2 py-1 text-sm text-primary placeholder:text-tertiary focus:border-border-focus focus:outline-none"
          />
          <span className="text-role-metadata">
            Set when this user's Odoo res.users.login differs from their
            SPI/Workspace email — typical for outside reps onboarded
            before they had an SPI account. Leave blank otherwise.
          </span>
        </label>
      </div>
    </Modal>
  )
}
